import React from "react";
import styled from "styled-components";
import { AppAnchor } from "../common/CommonStyles";
import { useLiveData } from "../common/utils";
import { useTranslation, Trans } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Account, AccountContext } from "../common/context/AccountContext";
import { isSchoolUserStudent, isSchoolUser } from "../common/api";
import {
  COLOR_PURPLE_DEEP,
  COLOR_PURPLE_BRIGHT,
  COLOR_TEXT_PRIMARY,
  COLOR_WHITE,
  COLOR_RED,
  COLOR_BLACK,
} from "../common/colors";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons";

const BannerContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 0 16px;

  background-image: linear-gradient(to bottom, ${COLOR_PURPLE_DEEP}, ${COLOR_PURPLE_BRIGHT});
`;

const TeacherImage = styled.img`
  max-width: 400px;
  height: 100%;
  filter: drop-shadow(0 0 15px ${COLOR_WHITE});
`;

const ConnectWithUsButton = styled.button`
  width: fit-content;
  background-color: ${COLOR_TEXT_PRIMARY};
  border: 2px solid ${COLOR_WHITE};
  border-radius: 8px;
  color: ${COLOR_WHITE};
  cursor: pointer;
  padding: 8px 20px;
  font-size: min(14px, 2rem);
  font-weight: 500;

  display: flex;
  align-items: center;
  gap: 6px;
`;

const HighlightedText = styled.span`
  color: ${COLOR_RED};
  font-weight: 700;
`;

const SpeechBubble = styled.div`
  background-color: ${COLOR_WHITE};
  border-radius: 12px;
  position: relative;
  padding: 8px;
  color: ${COLOR_BLACK};

  text-align: center;

  br {
    display: block;
    content: "";
    margin-top: -4px;
  }
`;

export const UpsellBanner: React.FC = () => {
  const { t } = useTranslation();
  const [account, setAccount] = React.useState<Account>();
  const { accountLiveData } = React.useContext(AccountContext);
  const todayUTC = new Date();
  todayUTC.setUTCHours(0, 0, 0, 0);

  useLiveData(
    React.useCallback(async (account: Account) => {
      if (account.hasMaybeProfileState()) {
        setAccount(account);
      }
    }, []),
    accountLiveData
  );

  if (
    account === undefined ||
    !isSchoolUser(account.user) ||
    (account.user.school_pilot_end_date === null &&
      account.user.school_access_end_date === null)
  ) {
    return <></>;
  }

  const endDateString =
    account.user.school_access_end_date ?? account.user.school_pilot_end_date;
  if (endDateString === null) {
    return <></>;
  }

  // Don't show banner for students unless it's for a pilot ending.
  const isForPilot = account.user.school_pilot_end_date !== null;
  if (!isForPilot && isSchoolUserStudent(account.user)) {
    return <></>;
  }

  const expired = new Date(endDateString) <= todayUTC;
  let daysUntilExpiration = 0;

  if (!expired) {
    const endDate = new Date(endDateString);
    daysUntilExpiration =
      (endDate.getTime() - todayUTC.getTime()) / (1000 * 60 * 60 * 24);
  }

  if (!isForPilot && daysUntilExpiration > 30) {
    return <></>;
  }

  return (
    <AppAnchor
      target="_blank"
      rel="noopener noreferrer"
      href="https://getsuperteacher.com/schools/signup/"
    >
      <BannerContainer>
        <div className="d-none d-lg-block">
          <TeacherImage src={`${process.env.PUBLIC_URL}/teacher_gradient_upsell.png`} />
        </div>
        <div className="d-flex flex-column justify-content-evenly align-items-center p-2 gap-2">
          <SpeechBubble>
            {(() => {
              if (expired) {
                if (isForPilot) {
                  if (isSchoolUserStudent(account.user)) {
                    return (
                      <Trans
                        t={t}
                        i18nKey="school_trial_upsell_banner_trial_ended_student"
                      />
                    );
                  } else {
                    return (
                      <Trans
                        t={t}
                        i18nKey="school_trial_upsell_banner_trial_ended"
                      />
                    );
                  }
                } else {
                  return (
                    <Trans
                      t={t}
                      i18nKey="school_access_upsell_banner_ended"
                    />
                  );
                }
              }
              if (daysUntilExpiration > 0) {
                return (
                  <Trans
                    t={t}
                    i18nKey={
                      isForPilot
                        ? "school_trial_upsell_banner_days_remaining"
                        : "school_access_upsell_banner_days_remaining"
                    }
                    components={{ highlight: <HighlightedText />, br: <br /> }}
                    values={{ count: daysUntilExpiration }}
                  />
                );
              }
              return (
                <Trans
                  t={t}
                  i18nKey={
                    isForPilot
                      ? "school_trial_upsell_banner"
                      : "school_access_upsell_banner"
                  }
                  components={{ highlight: <HighlightedText />, br: <br /> }}
                />
              );
            })()}
          </SpeechBubble>
          {!isSchoolUserStudent(account.user) && (
            <ConnectWithUsButton>
              {t("upsell_banner_connect_with_us")}
              <FontAwesomeIcon icon={faArrowRight} />
            </ConnectWithUsButton>
          )}
        </div>
      </BannerContainer>
    </AppAnchor>
  );
};
